import type { Ref } from "vue";
import { computed, ref, watch } from "vue";

import {
  type ThreadSection,
  filterThreadsByQuery,
  groupThreadsByActivity,
} from "@/lib/discussion-thread-list";
import type {
  DiscussionMessage,
  DiscussionThread,
  MentionableEntity,
} from "@/types";

import type { DiscussionThreadAttachment } from "../types";

interface UseDiscussionThreadsSelectionOptions {
  threadsActive: Ref<DiscussionThread[]>;
  threadsArchived: Ref<DiscussionThread[]>;
  selectedThreadProp: Ref<DiscussionThread | null | undefined>;
  selectedThreadRef?: Ref<DiscussionThread | null>;
  messagesThreadIdProp: Ref<number | null | undefined>;
  messagesProp: Ref<DiscussionMessage[] | undefined>;
  mentionablesProp: Ref<MentionableEntity[] | undefined>;
  attachmentsProp: Ref<DiscussionThreadAttachment[] | undefined>;
  onSelectedThreadChange?: (
    incoming: DiscussionThread | null,
    previous: DiscussionThread | null,
  ) => void;
  onSelectedThreadMissing?: () => void;
}

const threadListSignature = (threads: DiscussionThread[]) =>
  threads
    .map((thread) => `${thread.id}:${thread.updated_at}:${thread.archived ? 1 : 0}`)
    .join("|");

export function useDiscussionThreadsSelection({
  threadsActive,
  threadsArchived,
  selectedThreadProp,
  selectedThreadRef,
  messagesThreadIdProp,
  messagesProp,
  mentionablesProp,
  attachmentsProp,
  onSelectedThreadChange,
  onSelectedThreadMissing,
}: UseDiscussionThreadsSelectionOptions) {
  const searchValue = ref("");

  const selectedThread =
    selectedThreadRef ?? ref<DiscussionThread | null>(selectedThreadProp.value ?? null);
  const selectedMessages = ref<DiscussionMessage[]>([]);
  const selectedMentionables = ref<MentionableEntity[]>([]);
  const selectedAttachments = ref<DiscussionThreadAttachment[]>([]);

  const clearSelectedThreadPayload = () => {
    selectedMessages.value = [];
    selectedMentionables.value = [];
    selectedAttachments.value = [];
  };

  const applySelectedThreadPayload = () => {
    const selectedThreadId = selectedThread.value?.id ?? null;
    const messagesThreadId = messagesThreadIdProp.value ?? null;

    if (!selectedThreadId || messagesThreadId !== selectedThreadId) {
      clearSelectedThreadPayload();
      return;
    }

    const incomingMessages = messagesProp.value ?? [];
    const hasCrossThreadMessages = incomingMessages.some(
      (message) => message.thread_id !== selectedThreadId,
    );

    if (hasCrossThreadMessages) {
      clearSelectedThreadPayload();
      return;
    }

    selectedMessages.value = [...incomingMessages];
    selectedMentionables.value = [...(mentionablesProp.value ?? [])];
    selectedAttachments.value = [...(attachmentsProp.value ?? [])];
  };

  watch(selectedThreadProp, (incoming) => {
    const previous = selectedThread.value;
    selectedThread.value = incoming ?? null;
    onSelectedThreadChange?.(incoming ?? null, previous);
  });

  watch(
    () => [threadListSignature(threadsActive.value), threadListSignature(threadsArchived.value)],
    () => {
      const incomingThreads = [...threadsActive.value, ...threadsArchived.value];

      if (incomingThreads.length === 0) {
        selectedThread.value = null;
        onSelectedThreadMissing?.();
        return;
      }

      if (!selectedThread.value) return;

      const freshThread = incomingThreads.find(
        (thread) => thread.id === selectedThread.value?.id,
      );

      if (!freshThread) {
        selectedThread.value = null;
        onSelectedThreadMissing?.();
      } else {
        // Merge fresh list data (archived, title, updated_at, etc.) while
        // preserving fields only present on the selected thread (participants).
        selectedThread.value = { ...selectedThread.value, ...freshThread };
      }
    },
  );

  watch(
    [
      () => selectedThread.value?.id,
      messagesThreadIdProp,
      messagesProp,
      mentionablesProp,
      attachmentsProp,
    ],
    () => {
      applySelectedThreadPayload();
    },
    { immediate: true },
  );

  const filteredActiveThreads = computed(() =>
    filterThreadsByQuery(threadsActive.value, searchValue.value),
  );
  const filteredArchivedThreads = computed(() =>
    filterThreadsByQuery(threadsArchived.value, searchValue.value),
  );

  const threadSections = computed<ThreadSection[]>(() => [
    {
      key: "active",
      label: "Activos",
      groups: groupThreadsByActivity(filteredActiveThreads.value),
      emptyMessage: "Sin hilos activos.",
    },
    {
      key: "archived",
      label: "Archivados",
      groups: groupThreadsByActivity(filteredArchivedThreads.value),
      emptyMessage: "Sin hilos archivados.",
    },
  ]);

  const selectedParticipantsUsers = computed(
    () => selectedThread.value?.participants?.users ?? [],
  );
  const selectedParticipantsGroups = computed(
    () => selectedThread.value?.participants?.groups ?? [],
  );
  const selectedCreatorName = computed(
    () => selectedThread.value?.creator.name ?? "owner",
  );
  const selectedCreatorEmail = computed(
    () => selectedThread.value?.creator.email ?? "owner@example.com",
  );
  const selectedDescription = computed(
    () =>
      selectedThread.value?.description?.trim() ||
      "Todavía no hay descripción para este hilo.",
  );
  const selectedCreatedAt = computed(() => selectedThread.value?.created_at ?? "");
  const selectedUpdatedAt = computed(() => selectedThread.value?.updated_at ?? "");
  const selectedTitle = computed(
    () => selectedThread.value?.title ?? "Selecciona un hilo",
  );

  return {
    searchValue,
    selectedThread,
    clearSelectedThreadPayload,
    selectedMessages,
    selectedMentionables,
    selectedAttachments,
    threadSections,
    selectedParticipantsUsers,
    selectedParticipantsGroups,
    selectedCreatorName,
    selectedCreatorEmail,
    selectedDescription,
    selectedCreatedAt,
    selectedUpdatedAt,
    selectedTitle,
  };
}
